import { useEffect, useState } from "react";
import "./App.css";

import { AccountPage } from "./components/AccountPage";
import { AllocationSection } from "./components/AllocationSection";
import { AmountSection } from "./components/AmountSection";
import { AmplifyImpactSection } from "./components/AmplifyImpactSection";
import { CommunicationsSection } from "./components/CommunicationsSection";
import { DonateButtonSection } from "./components/DonateButtonSection";
import { DonationFrequencySection } from "./components/DonationFrequencySection";
import { Header } from "./components/Header";
import { Loader } from "./components/Loader";
import { PaymentMethodSection } from "./components/PaymentMethodSection";
import { PersonalDetailsSection } from "./components/PersonalDetailsSection";
import { SpecificAllocationSection } from "./components/SpecificAllocationSection";
import { ThankYouSection } from "./components/ThankYouSection";
import { TotalAmountSection } from "./components/TotalAmountSection";
import { VerificationCodeSection } from "./components/VerificationCodeSection";
import { useCharities } from "./hooks/useCharities";
import { useFormState } from "./hooks/useFormState";
import ApiService, {
	type DonationRequest,
	type SessionInfo,
} from "./services/api";
import { centsToDollars } from "./types";

export function App() {
	const {
		charities,
		loading: charitiesLoading,
		error: charitiesError,
	} = useCharities();
	const {
		formData,
		updateFormData,
		setDonationFrequency,
		setAllocationType,
		setBasicDonationAmount,
		setCharityAllocation,
		setTipValues,
		getSpecificAllocationsTotalDollars,
		// Validation methods
		touchField,
		validateSingleField,
		validateForm,
		getFieldError,
		hasFieldError,
	} = useFormState(charities);

	// Compute total donation and check limit
	const totalDonationDollars =
		formData.allocationType === "specific"
			? getSpecificAllocationsTotalDollars() + formData.tipAmountCents / 100
			: formData.basicDonationAmountCents / 100 + formData.tipAmountCents / 100;
	const exceedsLimit = totalDonationDollars > 150000;

	const [isLoading, setIsLoading] = useState(false);
	const [showThankYou, setShowThankYou] = useState(false);
	const [bankTransferInfo, setBankTransferInfo] = useState<{
		reference: string;
		amount: string;
		email: string;
	} | null>(null);
	const [showVerification, setShowVerification] = useState(false);
	const [_verifiedUserId, setVerifiedUserId] = useState<number | null>(null);
	const [cooldownSeconds, setCooldownSeconds] = useState<number>(0);
	const [verificationError, setVerificationError] = useState<string | null>(
		null,
	);

	// Sign-in flow state
	const [showSignIn, setShowSignIn] = useState(false);
	const [signInEmail, setSignInEmail] = useState("");
	const [showSignInVerification, setShowSignInVerification] = useState(false);
	const [signInError, setSignInError] = useState<string | null>(null);

	// Session and view management
	const [currentView, setCurrentView] = useState<"donation" | "account">(
		"donation",
	);
	const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
	const [sessionLoading, setSessionLoading] = useState<boolean>(true);

	// Check for active session on mount
	useEffect(() => {
		const checkSession = async () => {
			try {
				const session = await ApiService.tryValidateSessionSilently();
				if (session) {
					setSessionInfo(session);
					// Prefill communication preferences from user's stored settings
					if (session.user_details) {
						updateFormData({
							subscribeToUpdates: session.user_details.subscribe_to_updates,
						});
					}
				}
			} finally {
				setSessionLoading(false);
			}
		};
		checkSession();
	}, [updateFormData]);

	// Check URL parameters for thank you page display
	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const success = urlParams.get("success");
		const cancelled = urlParams.get("cancelled");

		if (success === "true") {
			setShowThankYou(true);
			// Clean up URL without page reload
			window.history.replaceState({}, "", window.location.pathname);
		} else if (cancelled === "true") {
			// Handle cancelled payment - could show a message
			window.history.replaceState({}, "", window.location.pathname);
			alert("Payment was cancelled. You can try again.");
		}
	}, []);

	const buildApiFormData = (): DonationRequest => {
		// Build allocations array
		const allocations: Array<{
			charity_id: number;
			amount_cents: number;
		}> = [];

		if (formData.allocationType === "specific") {
			// Add specific charity allocations
			Object.entries(formData.specificAllocationsCents).forEach(
				([charityId, amountCents]) => {
					if (amountCents > 0) {
						allocations.push({
							charity_id: Number(charityId),
							amount_cents: amountCents,
						});
					}
				},
			);
		} else {
			// Add basic donation amount - GiveWell All Grants Fund
			const allGrantsFund = charities.find(
				(c) => c.name === "GiveWell All Grants Fund",
			);
			if (formData.basicDonationAmountCents > 0) {
				if (!allGrantsFund) {
					console.error(
						"Could not find 'GiveWell All Grants Fund' in charity list",
						charities,
					);
					throw new Error(
						"GiveWell All Grants Fund not found. Please contact support.",
					);
				}
				allocations.push({
					charity_id: allGrantsFund.id,
					amount_cents: formData.basicDonationAmountCents,
				});
			}
		}

		// Add tip amount - GiveWise operational support
		if (formData.tipAmountCents > 0) {
			const giveWise = charities.find((c) => c.name === "GiveWise");
			if (!giveWise) {
				console.error("Could not find 'GiveWise' in charity list", charities);
				throw new Error("GiveWise not found. Please contact support.");
			}
			allocations.push({
				charity_id: giveWise.id,
				amount_cents: formData.tipAmountCents,
			});
		}

		// Only include user details if user doesn't have them stored
		const userDetails =
			!sessionInfo || !sessionInfo.user_details
				? {
						first_name: formData.firstName,
						last_name: formData.lastName,
						country: formData.country,
						referral_source: formData.howDidYouHearAboutUs, // Required field, validated before submission
						subscribe_to_updates: formData.subscribeToUpdates,
					}
				: undefined;

		return {
			payment_method: formData.paymentMethod,
			recurring: formData.recurring,
			allocations,
			user_details: userDetails,
		};
	};

	const handleFormSubmit = async () => {
		if (exceedsLimit) {
			alert(
				"Contact info@givewise.org.hk for donations exceeding 150,000 HKD.",
			);
			return;
		}
		// Validation
		if (formData.allocationType === "specific") {
			const total =
				getSpecificAllocationsTotalDollars() + formData.tipAmountCents / 100;
			if (total < 4) {
				alert(
					"Please allocate at least 4 HKD across your preferred charities.",
				);
				return;
			}
		} else {
			const total =
				formData.basicDonationAmountCents / 100 + formData.tipAmountCents / 100;
			if (total < 4) {
				alert("Please select an amount of at least 4 HKD.");
				return;
			}
		}

		// If user is authenticated and has personal details, skip verification
		if (sessionInfo?.user_details) {
			// Already verified, submit directly
			await submitDonation();
		} else {
			// Need to verify email first (or collect personal details for new users)
			// Validate form before creating account
			const validationResult = validateForm();
			if (!validationResult.isValid) {
				const errorMessages = validationResult.errors
					.map((error) => `• ${error.message}`)
					.join("\n");
				alert(
					`Please fix the following validation errors before submitting:\n\n${errorMessages}`,
				);
				return;
			}
			await handleCreateAccount();
		}
	};

	const handleCreateAccount = async () => {
		setIsLoading(true);
		setVerificationError(null);

		try {
			// Build user details from form data
			const userDetails = {
				first_name: formData.firstName,
				last_name: formData.lastName,
				country: formData.country,
				referral_source: formData.howDidYouHearAboutUs,
				subscribe_to_updates: formData.subscribeToUpdates,
			};

			const response = await ApiService.createAccount(
				formData.email,
				userDetails,
			);

			if (response.success) {
				// Show verification code input
				setShowVerification(true);
				setIsLoading(false);
			} else {
				// Rate limited - show cooldown
				setIsLoading(false);
				setCooldownSeconds(response.cooldown_seconds || 0);
				setVerificationError(response.message);
			}
		} catch (error) {
			setIsLoading(false);
			console.error("Error creating account:", error);
			const errorMessage =
				error instanceof Error ? error.message : "Failed to create account.";

			// Check if this is an "account exists" error
			if (errorMessage.includes("already exists")) {
				setVerificationError(
					"An account with this email already exists. Please sign in instead.",
				);
			} else {
				setVerificationError(errorMessage);
			}
		}
	};

	const submitDonation = async () => {
		setIsLoading(true);
		try {
			// Build form data for API
			const apiData = buildApiFormData();

			// Call appropriate endpoint based on payment method
			// Session cookie is automatically sent by browser (credentials: "include")
			if (formData.paymentMethod === "credit-card") {
				const result = await ApiService.submitCreditCardDonation(apiData);
				setIsLoading(false);

				// Redirect to Stripe checkout URL
				console.log(`Redirecting to Stripe checkout: ${result.checkout_url}`);
				window.location.href = result.checkout_url;
				// Note: User will be redirected away from this page to Stripe
				// They'll return via success/cancel URLs configured in backend
			} else {
				const result = await ApiService.submitBankTransferDonation(apiData);
				setIsLoading(false);

				// Bank transfer - show thank you page with bank transfer info
				const reference = `GW-${result.donation_id.toString().padStart(8, "0")}`;
				const amount = `${(apiData.allocations.reduce((sum, a) => sum + a.amount_cents, 0) / 100).toLocaleString()} HKD`;
				setBankTransferInfo({ reference, amount, email: formData.email });
				setShowThankYou(true);
			}
		} catch (error) {
			setIsLoading(false);

			// Handle session expiry - ask user to verify email again
			const errorMessage =
				error instanceof Error ? error.message : "Unknown error";

			if (
				errorMessage.includes("session") ||
				errorMessage.includes("verify your email")
			) {
				// Session expired or invalid - restart verification flow
				console.log("Session expired, requesting new verification");
				setShowVerification(false);
				setVerifiedUserId(null);
				setSessionInfo(null);
				alert(
					"Your session has expired. Please verify your email again to continue.",
				);
				// Automatically request new code
				await handleCreateAccount();
			} else {
				// Other error
				console.error("Donation submission error:", error);
				alert(`Error processing donation: ${errorMessage}`);
			}
		}
	};

	const handleVerificationSuccess = async (userId: number) => {
		setVerifiedUserId(userId);
		setShowVerification(false);

		// Update session info after successful verification
		const session = await ApiService.validateSession();
		if (session) {
			setSessionInfo(session);
			// Load user's communication preferences if they exist
			if (session.user_details) {
				updateFormData({
					subscribeToUpdates: session.user_details.subscribe_to_updates,
				});
			}
		}

		await submitDonation();
	};

	// Sign-in flow handlers
	const handleSignInClick = () => {
		setShowSignIn(true);
		setSignInEmail("");
		setSignInError(null);
		setShowSignInVerification(false);
	};

	const handleSignInSubmit = async () => {
		if (!signInEmail.trim()) {
			setSignInError("Please enter your email address");
			return;
		}

		setIsLoading(true);
		setSignInError(null);

		try {
			const response = await ApiService.signIn(signInEmail);

			if (response.success) {
				setShowSignInVerification(true);
				setCooldownSeconds(0);
			} else {
				setCooldownSeconds(response.cooldown_seconds || 0);
				setSignInError(response.message);
			}
		} catch (error) {
			console.error("Error signing in:", error);
			const errorMessage =
				error instanceof Error ? error.message : "Failed to sign in.";

			if (errorMessage.includes("No account found")) {
				setSignInError(
					"No account found with this email. Please create an account by making a donation.",
				);
			} else {
				setSignInError(errorMessage);
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handleSignInVerificationSuccess = async (userId: number) => {
		setVerifiedUserId(userId);
		setShowSignIn(false);
		setShowSignInVerification(false);

		// Update session info after successful sign-in
		const session = await ApiService.validateSession();
		if (session) {
			setSessionInfo(session);
			// Load user's communication preferences if they exist
			if (session.user_details) {
				updateFormData({
					subscribeToUpdates: session.user_details.subscribe_to_updates,
				});
			}
		}
	};

	const handleSignInCancel = () => {
		setShowSignIn(false);
		setShowSignInVerification(false);
		setSignInEmail("");
		setSignInError(null);
	};

	// Render account page
	if (currentView === "account" && sessionInfo) {
		return (
			<>
				<Header
					hasActiveSession={!!sessionInfo}
					userEmail={sessionInfo.email}
					onAccountClick={() => setCurrentView("account")}
					onSignInClick={handleSignInClick}
					onLogoClick={() => setCurrentView("donation")}
				/>
				<AccountPage
					userEmail={sessionInfo.email}
					onBack={() => setCurrentView("donation")}
				/>
			</>
		);
	}

	if (showThankYou) {
		return (
			<>
				<Header
					hasActiveSession={!!sessionInfo}
					userEmail={sessionInfo?.email}
					onAccountClick={() => setCurrentView("account")}
					onSignInClick={handleSignInClick}
					onLogoClick={() => setCurrentView("donation")}
				/>
				<div className="donation-form-host">
					<ThankYouSection
						isVisible={true}
						bankTransferInfo={bankTransferInfo}
					/>
				</div>
			</>
		);
	}

	if (charitiesLoading || sessionLoading) {
		return (
			<>
				<Header
					hasActiveSession={!!sessionInfo}
					userEmail={sessionInfo?.email}
					onAccountClick={() => setCurrentView("account")}
					onSignInClick={handleSignInClick}
					onLogoClick={() => setCurrentView("donation")}
				/>
				<div className="donation-form-host">
					<div style={{ textAlign: "center", padding: "2rem" }}>
						<h2>Loading donation form...</h2>
						<p>Checking session and fetching charity information...</p>
					</div>
				</div>
			</>
		);
	}

	if (charitiesError) {
		return (
			<>
				<Header
					hasActiveSession={!!sessionInfo}
					userEmail={sessionInfo?.email}
					onAccountClick={() => setCurrentView("account")}
					onSignInClick={handleSignInClick}
					onLogoClick={() => setCurrentView("donation")}
				/>
				<div className="donation-form-host">
					<div style={{ textAlign: "center", padding: "2rem" }}>
						<h2>Error loading donation form</h2>
						<p>Unable to fetch charity data: {charitiesError}</p>
						<p>Please refresh the page or try again later.</p>
					</div>
				</div>
			</>
		);
	}

	return (
		<>
			<Header
				hasActiveSession={!!sessionInfo}
				userEmail={sessionInfo?.email}
				onAccountClick={() => setCurrentView("account")}
				onSignInClick={handleSignInClick}
				onLogoClick={() => setCurrentView("donation")}
			/>

			{/* Sign-in Modal */}
			{showSignIn && (
				<div
					role="dialog"
					aria-modal="true"
					aria-labelledby="sign-in-title"
					className="modal-overlay"
					onClick={handleSignInCancel}
					onKeyDown={(e) => e.key === "Escape" && handleSignInCancel()}
				>
					<div
						role="document"
						className="modal-content"
						onClick={(e) => e.stopPropagation()}
						onKeyDown={(e) => e.stopPropagation()}
					>
						{!showSignInVerification ? (
							<>
								<h2 id="sign-in-title" className="modal-title">
									Sign In
								</h2>
								<p className="modal-description">
									Enter your email address to sign in to your account.
								</p>

								<label htmlFor="sign-in-email">
									Email Address
									<input
										id="sign-in-email"
										type="email"
										value={signInEmail}
										onChange={(e) => setSignInEmail(e.target.value)}
										placeholder="your@email.com"
										className="modal-input"
										disabled={isLoading}
									/>
								</label>

								{signInError && (
									<div className="modal-error">{signInError}</div>
								)}

								<div className="modal-actions">
									<button
										type="button"
										onClick={handleSignInCancel}
										className="modal-button-secondary"
									>
										Cancel
									</button>
									<button
										type="button"
										onClick={handleSignInSubmit}
										disabled={isLoading || !signInEmail.trim()}
										className="modal-button-primary"
									>
										{isLoading ? "Sending..." : "Send Code"}
									</button>
								</div>
							</>
						) : (
							<>
								<button
									type="button"
									onClick={() => setShowSignInVerification(false)}
									className="modal-back-button"
								>
									← Back
								</button>
								<VerificationCodeSection
									email={signInEmail}
									isVisible={true}
									onVerificationSuccess={handleSignInVerificationSuccess}
									onRequestNewCode={handleSignInSubmit}
									cooldownSeconds={cooldownSeconds}
									errorMessage={signInError || undefined}
								/>
							</>
						)}
					</div>
				</div>
			)}

			<div className="donation-form-host">
				<form
					onSubmit={(e) => {
						e.preventDefault();
						handleFormSubmit();
					}}
				>
					<DonationFrequencySection
						recurring={formData.recurring}
						onRecurringChange={setDonationFrequency}
						hasActiveSubscription={sessionInfo?.has_active_subscription}
					/>

					<PaymentMethodSection
						paymentMethod={formData.paymentMethod}
						recurring={formData.recurring}
						onPaymentMethodChange={(value) =>
							updateFormData({ paymentMethod: value })
						}
					/>

					<AllocationSection
						allocationType={formData.allocationType}
						onAllocationTypeChange={setAllocationType}
					/>

					<AmountSection
						basicDonationAmount={centsToDollars(
							formData.basicDonationAmountCents,
						)}
						onAmountChange={setBasicDonationAmount}
						isVisible={formData.allocationType === "default"}
						recurring={formData.recurring}
					/>

					<SpecificAllocationSection
						charities={charities}
						allocations={Object.fromEntries(
							Object.entries(formData.specificAllocationsCents).map(
								([key, cents]) => [key, centsToDollars(cents)],
							),
						)}
						onAllocationChange={setCharityAllocation}
						isVisible={formData.allocationType === "specific"}
					/>

					<AmplifyImpactSection
						tipSize={formData.tipSize}
						tipType={formData.tipType}
						onTipChange={setTipValues}
					/>

					<TotalAmountSection
						formData={formData}
						charities={charities}
						exceedsLimit={exceedsLimit}
					/>

					{/* Only show personal details form if user doesn't have them stored */}
					{(!sessionInfo || !sessionInfo.user_details) && (
						<>
							<PersonalDetailsSection
								firstName={formData.firstName}
								lastName={formData.lastName}
								email={formData.email}
								country={formData.country}
								onFirstNameChange={(value) =>
									updateFormData({ firstName: value })
								}
								onLastNameChange={(value) =>
									updateFormData({ lastName: value })
								}
								onEmailChange={(value) => updateFormData({ email: value })}
								onCountryChange={(value) => updateFormData({ country: value })}
								getFieldError={getFieldError}
								hasFieldError={hasFieldError}
								touchField={touchField as (fieldName: string) => void}
								validateSingleField={
									validateSingleField as (
										fieldName: string,
										value: string,
									) => void
								}
							/>

							<CommunicationsSection
								subscribeToUpdates={formData.subscribeToUpdates}
								howDidYouHearAboutUs={formData.howDidYouHearAboutUs}
								onSubscribeToUpdatesChange={(value) =>
									updateFormData({ subscribeToUpdates: value })
								}
								onHowDidYouHearAboutUsChange={(value) =>
									updateFormData({ howDidYouHearAboutUs: value })
								}
								getFieldError={getFieldError}
								hasFieldError={hasFieldError}
								touchField={touchField as (fieldName: string) => void}
								validateSingleField={
									validateSingleField as (
										fieldName: string,
										value: string | number | boolean,
									) => void
								}
							/>
						</>
					)}

					{/* Show logged in message for returning users */}
					{sessionInfo?.user_details && (
						<div className="form-section">
							<h2>Your Account</h2>
							<p>
								You're donating as <strong>{sessionInfo.email}</strong>.
							</p>
						</div>
					)}

					<DonateButtonSection
						onSubmit={handleFormSubmit}
						isLoading={isLoading}
					/>

					<VerificationCodeSection
						email={formData.email}
						isVisible={showVerification}
						onVerificationSuccess={handleVerificationSuccess}
						onRequestNewCode={handleCreateAccount}
						cooldownSeconds={cooldownSeconds}
						errorMessage={verificationError || undefined}
					/>
				</form>
			</div>

			<Loader isVisible={isLoading} />
		</>
	);
}
